<template>
  <div v-if="state.type === 'open'" class="bg">
    <div
      v-click-outside="close"
      class="search"
      role="search"
      aria-modal="true"
      :class="{ 'with-completions': state.status.type !== 'initial' }"
    >
      <input
        ref="inputEl"
        class="search-input"
        type="text"
        placeholder="Search…"
        @input="onInput"
        @keydown="onKeyDown"
      />
      <template v-if="state.status.type !== 'initial'">
        <div class="spacer" />
        <div
          class="results"
          :class="{ loading: state.status.type === 'loading' }"
        >
          <template v-if="state.status.type === 'results'">
            <SearchResultsList
              ref="searchResultsList"
              :results="state.status.results"
              :show-global-context="true"
              @choose="onResultChosen"
            />
          </template>
          <BaseNoResults
            v-if="state.status.type === 'loading'"
            :is-loading="true"
            class="wrapper-no-results"
          />
          <BaseNoResults
            v-if="state.status.type === 'no-results'"
            :is-loading="false"
            class="wrapper-no-results"
          />
          <div v-if="state.status.type === 'error'">Error</div>
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import { throttle } from "lodash-es";
import { defineComponent, nextTick, onBeforeUnmount, ref, watch } from "vue";

import assert from "@/assert";
import BaseNoResults from "@/components/base/BaseNoResults.vue";
import * as inject from "@/components/inject";
import { metrics } from "@/metrics";
import { navigateToId, navigateToObject } from "@/utils/navigate";

import { Result, searchResultsToResults } from "./search";
import SearchResultsList from "./search/SearchResultsList.vue";

type State =
  | {
      type: "open";
      query: string;
      status:
        | { type: "initial" }
        | { type: "loading" }
        | {
            type: "results";
            results: Result[];
          }
        | { type: "no-results" }
        | { type: "error" };
    }
  | { type: "closed" };

const openState: State = {
  type: "open",
  query: "",
  status: { type: "initial" },
};
const closedState: State = {
  type: "closed",
};

export default defineComponent({
  name: "ModalSearch",
  components: { BaseNoResults, SearchResultsList },
  setup() {
    const controller = inject.controller();
    const defaultTypes = inject.personalDefaultTypes();
    const router = inject.router();
    // Need to sync isInputFocused from here to avoid global shortcuts when this
    // modal is open
    const isInputFocused = inject.isInputFocused();
    const inputEl = ref<HTMLInputElement | null>(null);
    const state = ref<State>(closedState);
    const searchResultsList = ref<InstanceType<
      typeof SearchResultsList
    > | null>(null);

    onBeforeUnmount(() => {
      isInputFocused.value = false;
    });

    const search = throttle(
      async () => {
        // Ignore if not open, this callback may get triggered after the modal
        // closes because it is debounced.
        if (state.value.type === "open") {
          const query = state.value.query;

          state.value = {
            type: "open",
            query,
            status: { type: "loading" },
          };

          try {
            const results = await controller.search(null, query, 50);
            if (state.value.type === "open" && state.value.query === query) {
              if (results.length === 0) {
                state.value = {
                  type: "open",
                  query,
                  status: { type: "no-results" },
                };
              } else {
                state.value = {
                  type: "open",
                  query,
                  status: {
                    type: "results",
                    results: searchResultsToResults(results, query),
                  },
                };

                metrics.segment.track("Search Queried", {
                  action: "queried",
                  query: query,
                  n_results: results.length,
                });
              }
            } else {
              // Search discarded, results from previous query
            }
          } catch {
            state.value = {
              type: "open",
              query,
              status: { type: "error" },
            };
          }
        }
      },
      300,
      { leading: false, trailing: true }
    );

    watch(state, (state, prevState) => {
      if (state.type === "open") {
        // Search when the query changes and is not empty
        if (
          prevState.type === "open" &&
          prevState.query !== state.query &&
          state.query !== ""
        ) {
          search();
        }
      }
    });

    const close = () => {
      metrics.segment.track("Search Closed", { action: "closed" });

      state.value = closedState;
      isInputFocused.value = false;
    };

    const open = () => {
      metrics.segment.track("Search Opened", { action: "opened" });

      state.value = openState;
      nextTick(() => {
        inputEl.value?.focus();
        isInputFocused.value = true;
      });
    };

    const toggle = () => {
      if (state.value.type === "open") close();
      else open();
    };

    const onInput = (e: Event) => {
      if (!(e.target instanceof HTMLInputElement)) return;
      const el = e.target;
      const query = el.value;
      if (state.value.type === "open") {
        state.value = {
          ...state.value,
          query,
          status:
            query === "" && state.value.status.type !== "initial"
              ? { type: "initial" }
              : state.value.status,
        };
      }
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        close();
      } else {
        searchResultsList.value?.onKeyDown(event);
      }
    };

    const onResultChosen = async (selected: number) => {
      assert(
        state.value.type === "open",
        "Modal should be open to select a result"
      );
      assert(
        state.value.status.type === "results",
        "Modal should have results to select from"
      );

      const { searchResult, id } = state.value.status.results[selected];

      close();

      let query;
      if (searchResult.id.location?.location === "notes") {
        query = { "focus-block": searchResult.id.location.block };
      }
      if (id.kind === "object") {
        const {
          workspace_id: workspaceId,
          table_id: tableId,
          row_id: rowId,
        } = await controller.graph.object.id.parse(id.id);
        navigateToId(
          router,
          controller,
          defaultTypes.value || {},
          workspaceId || "",
          tableId,
          rowId
        );
      } else {
        navigateToObject(router, controller, id, undefined, query);
      }
    };

    return {
      state,
      inputEl,
      close,
      open,
      toggle,
      onInput,
      onKeyDown,
      onResultChosen,
      searchResultsList,
    };
  },
});
</script>

<style scoped lang="scss">
@use "@/styles/console.scss" as *;
.bg {
  position: fixed;
  z-index: $z-index-modal-low;
  top: 0px;
  inset-inline-start: 0px;
  width: 100%;
  height: 100vh;
  @include themed() {
    background: t($bg-overlay);
  }
}

.search {
  position: absolute;
  top: 15%;
  inset-inline-start: 50%;
  transform: translate(-50%, 0%);
  width: 740px;
  max-height: calc(100vh - 15% - 15%);
  padding: $paddingConsole;

  box-shadow: $console;
  box-sizing: border-box;
  border-radius: $border-radius-l;
  cursor: text;

  display: flex;
  flex-direction: column;

  @include themed() {
    background: t($bg);
    caret-color: t($secondary);
  }

  &.with-completions {
    padding-bottom: 0;
  }
}

.search-input {
  flex-shrink: 0;
  box-sizing: border-box;
  width: 100%;
  outline: none;
  border: none;
  overflow: hidden;
  padding: 0 $paddingInput;
  @extend %font-console-input;

  @include themed() {
    background: t($bg);
    color: t($primary);
  }

  &::placeholder {
    @include themed() {
      color: t($grey-40);
    }
  }
}

.spacer {
  flex-shrink: 0;
  width: 100%;
  height: 1px;
  margin: 12px 0 0 0;

  @include themed() {
    border-bottom: 1px solid t($grey-10);
  }
}

.results {
  flex: 1;
  padding: $paddingCompletions;
  max-height: 100%;
  overflow-y: auto;
  scrollbar-width: thin;

  &.loading {
    padding: 0;
  }
}

.wrapper-no-results {
  padding: $paddingCompletions;
}
</style>
